import { defineMessages } from 'react-intl-next';

import AkFeatureGates from '@atlaskit/feature-gate-js-client';

import { fg } from '@adminhub/feature-gating';
import { EditionType, type UiProductType } from '@adminhub/graphql-types';

import type { ProductKeyConverterValues } from '../orig/product-key-converter';
import { ProductKeyConverter } from '../orig/product-key-converter';
import { DEFAULT_ARI_RESOURCE_TYPE, loadProductConfiguration, ProductConfigInputOptionPriority } from '../types';
import type {
  CcpWacUrlPathArgs,
  OfferingEnvironment,
  OfferingsSchemaWithIds,
  ProductConfig,
  ProductConfigInputOptions,
  ProductConfigJson,
  ProductConfigMapJson,
  WacUrlPathArgs,
} from '../types';
import type { ConfigReverseMap } from './common';

// Product without description will use this default description
export const DEFAULT_DESCRIPTION = defineMessages({
  description: {
    id: 'add-product-siteless.select-product-step.card.defaultDescription.do-not-translate',
    defaultMessage: 'Default description',
    // eslint-disable-next-line @atlassian/i18n/require-description
    description: 'Default description',
  },
});
/**
 * Represents map of feature gates and their corresponding product overrides for a given product.
 * { featureGate: ProductConfigJson }
 */
type FeatureGateOverrides = Record<string, ProductConfigJson>;

/**
 * Helper function to check if a key in an object is explicitly set to undefined.
 *
 * @param obj Object to check
 * @param key Key within the object to check
 * @typeParam T Type of the object
 * @returns True if the key is explicitly set to undefined, false otherwise.
 */
function isExplicitlyUndefined<T extends object>(obj: T, key: keyof T): boolean {
  return key in obj && obj[key] === undefined;
}

/**
 * Helper function to flatten and deduplicate an arrays of values.
 *
 * @param values 2D arrays of values to be flattened and deduplicate
 * @returns A new flat array with unique values, excluding undefined values.
 */
function flatUnique<T>(...values: (T | undefined | (T | undefined)[])[]): T[] {
  return [...new Set(values.flat().filter((v): v is T => v !== undefined))];
}

// AkFeatureGates may not be initilized yet when the gate is initially checked (i.e. in analytics client), so as a workaround we catch the error and return false
function checkGate(featureGate: string): boolean {
  try {
    return AkFeatureGates.checkGate(featureGate);
  } catch (e) {
    return false;
  }
}

export class ProductConfigImpl implements ProductConfig {
  private readonly json: ProductConfigJson;
  private readonly featureGateOverridesPerProperty: Partial<{
    [K in keyof ProductConfigJson]: Record<string, ProductConfigJson[K]>;
  }>;

  constructor(
    readonly baseJson: ProductConfigJson,
    readonly featureGateOverrides?: FeatureGateOverrides,
  ) {
    // Create map of { property: { featureGate: value } }
    this.featureGateOverridesPerProperty = Object.entries(featureGateOverrides || {}).reduce((acc, [featureGate, productConfigJson]) => {
      for (const [property, value] of Object.entries(productConfigJson)) {
        if (!(property in acc)) {
          acc[property] = {};
        }
        acc[property][featureGate] = value;
      }

      return acc;
    }, {});

    // Create a proxy to override the properties of the default json with the feature gate overrides
    this.json = new Proxy(baseJson, {
      // Use arrow function to preserve the context of 'this'
      get: <K extends keyof ProductConfigJson>(target: ProductConfigJson, prop: K): ProductConfigJson[K] => {
        if (prop in this.featureGateOverridesPerProperty && typeof this.featureGateOverridesPerProperty[prop] === 'object') {
          for (const [featureGate, value] of Object.entries(this.featureGateOverridesPerProperty[prop])) {
            if (checkGate(featureGate)) {
              return value;
            }
          }
        }

        return target[prop];
      },
      has: (target: ProductConfigJson, prop: string): boolean => {
        if (prop in this.featureGateOverridesPerProperty && typeof this.featureGateOverridesPerProperty[prop] === 'object') {
          for (const featureGate of Object.keys(this.featureGateOverridesPerProperty[prop])) {
            if (checkGate(featureGate)) {
              return true;
            }
          }
        }

        return prop in target;
      },
    });
  }

  get defaultValue() {
    return this.json.ariResourceOwner;
  }

  get ariKey() {
    return this.json.ariResourceOwner;
  }

  get ariResourceType() {
    if (!this.json.ariResourceType) {
      return DEFAULT_ARI_RESOURCE_TYPE;
    }

    return this.json.ariResourceType;
  }

  get productName() {
    return this.json.productName;
  }

  get localizedProductName() {
    return this.json.localizedProductName;
  }

  get type() {
    // In order to support UiProductType, we will derive the type from the default value
    return this.json.type || (this.defaultValue.toUpperCase().replaceAll('-', '_') as UiProductType);
  }

  get productId() {
    if (isExplicitlyUndefined(this.json, 'userManagementProductId')) {
      throw new Error('userManagementProductId is not defined');
    }

    return this.json.userManagementProductId || this.defaultValue;
  }

  get ccpProductId() {
    return this.json.productId;
  }

  get key() {
    if (isExplicitlyUndefined(this.json, 'idOrgManagerProductKey')) {
      throw new Error('idOrgManagerProductKey is not defined');
    }

    return this.json.idOrgManagerProductKey || this.defaultValue;
  }

  get canonicalProductKey() {
    if (isExplicitlyUndefined(this.json, 'userManagementCanonicalProductKey')) {
      throw new Error('userManagementCanonicalProductKey is not defined');
    }

    return this.json.userManagementCanonicalProductKey || this.defaultValue;
  }

  get emauKey() {
    if (isExplicitlyUndefined(this.json, 'emauProductKey')) {
      throw new Error('emauProductKey is not defined');
    }

    return this.json.emauProductKey || this.defaultValue;
  }

  get hamsProductKey() {
    return this.json.hamsProductKey;
  }

  get sandboxProductKey() {
    if (isExplicitlyUndefined(this.json, 'sandboxProductKey')) {
      throw new Error('sandboxProductKey is not defined');
    }

    return this.json.sandboxProductKey || this.defaultValue;
  }

  get enterpriseProductKey() {
    if (!this.json.enterpriseProductKey) {
      throw new Error('enterpriseProductKey is not defined');
    }

    return this.json.enterpriseProductKey;
  }

  get isUiProductType() {
    if (AkFeatureGates.checkGate('aims_remove_old_product_key_convertor')) {
      return true;
    }

    // TODO: https://hello.jira.atlassian.cloud/browse/PKEY-1692 Used for fallback. Remove this after finishing migration
    return new ProductKeyConverter({
      canonicalProductKey: undefined,
      key: undefined,
      productId: undefined,
      type: this.type,
    }).isUiProductType;
  }

  get uiProductType() {
    if (AkFeatureGates.checkGate('aims_remove_old_product_key_convertor')) {
      return this.type;
    }

    // TODO: https://hello.jira.atlassian.cloud/browse/PKEY-1693 Used for fallback. Remove this after finishing migration
    return new ProductKeyConverter({
      canonicalProductKey: undefined,
      key: undefined,
      productId: undefined,
      type: this.type,
    }).uiProductType;
  }

  get getBundleOrProductType() {
    if (AkFeatureGates.checkGate('aims_remove_old_product_key_convertor')) {
      return this.type;
    }

    // TODO: https://hello.jira.atlassian.cloud/browse/PKEY-1614 Used for fallback. Remove this after finishing migration
    return new ProductKeyConverter({
      canonicalProductKey: undefined,
      key: undefined,
      productId: undefined,
      type: this.type,
    }).getBundleOrProductType;
  }

  get productLogo() {
    return this.json.productLogo;
  }

  get productIcon() {
    return this.json.productIcon;
  }

  get dcProductIcon() {
    return this.json.dcProductIcon;
  }

  get productSettingName() {
    if (isExplicitlyUndefined(this.json, 'productSettingName')) {
      return undefined;
    }

    return this.json.productSettingName || this.productName;
  }

  get links() {
    return this.json.links || {};
  }

  get signUpLink() {
    return this.json.signUpLink || 'https://www.atlassian.com/try';
  }

  get offerings() {
    return Object.entries(this.json.offerings ?? {}).reduce((acc, [key, offeringSchema]) => {
      acc[key] = {
        ...offeringSchema,
        offeringId: key,
      };

      return acc;
    }, {});
  }

  getEnterpriseOffering(environment?: OfferingEnvironment): OfferingsSchemaWithIds[string] | undefined {
    const isEnableAddAppInFedramp = fg('add-app-in-fedramp-env');

    if (!isEnableAddAppInFedramp) {
      for (const offering of Object.values<OfferingsSchemaWithIds[string]>(this.offerings)) {
        if (offering.edition === EditionType.enterprise) {
          return offering;
        }
      }

      return undefined;
    }

    return Object.values<OfferingsSchemaWithIds[string]>(this.offerings).find((offering) => {
      if (offering.edition !== EditionType.enterprise) {
        return false;
      }
      if (!environment) {
        return true;
      }

      return this.getOfferingEnvironment(offering) === environment;
    });
  }

  getEnterpriseCollectionAppOffering(environment?: OfferingEnvironment): OfferingsSchemaWithIds[string] | undefined {
    const isEnableAddAppInFedramp = fg('add-app-in-fedramp-env');

    if (!isEnableAddAppInFedramp) {
      for (const offering of Object.values<OfferingsSchemaWithIds[string]>(this.offerings)) {
        if (offering.edition === EditionType.enterprise && offering.parentOfferingId) {
          return offering;
        }
      }

      return undefined;
    }

    return Object.values<OfferingsSchemaWithIds[string]>(this.offerings).find((offering) => {
      // Checks that the enterprise collection app offering is an enterprise edition AND has a parentOfferingId
      if (offering.edition !== EditionType.enterprise || !offering.parentOfferingId) {
        return false;
      }

      if (!environment) {
        return true;
      }

      return this.getOfferingEnvironment(offering) === environment;
    });
  }

  getOffering() {
    for (const offering of Object.values<OfferingsSchemaWithIds[string]>(this.offerings)) {
      if (offering.edition !== EditionType.enterprise) {
        return offering;
      }
    }

    return undefined;
  }

  hasLinks() {
    return Object.keys(this.links).length !== 0;
  }

  get addProduct() {
    return this.json.addProduct && this.json.addProduct.enabled
      ? {
          enabled: this.json.addProduct.enabled,
          parentCollectionName: this.json.addProduct.parentCollectionName,
          productKey: this.json.addProduct.productKey,
          vortexOnly: this.json.addProduct.vortexOnly ?? false,
          nonVortexOnly: this.json.addProduct.nonVortexOnly ?? false,
          enterpriseProductKey: this.json.addProduct.enterpriseProductKey,
          productCard: this.json.addProduct.productCard,
          includeWithAccess: this.json.addProduct.includeWithAccess ?? false,
          includeWithExistingSites: this.json.addProduct.includeWithExistingSites ?? false,
          includeWithVortex: this.json.addProduct.includeWithVortex ?? false,
          includeWithJWMForMigrationFlow: this.json.addProduct.includeWithJWMForMigrationFlow ?? false,
          includeWithRovoPaidProvisioning: this.json.addProduct.includeWithRovoPaidProvisioning ?? false,
          includeWithJSM: this.json.addProduct.includeWithJSM ?? false,
          includeWithTWCEnterprise: this.json.addProduct.includeWithTWCEnterprise ?? false,
          welcomePath: this.json.addProduct.welcomePath,
        }
      : {
          enabled: false,
          productKey: this.defaultValue,
        };
  }

  get description() {
    return this.json.description || DEFAULT_DESCRIPTION;
  }

  get summaryPoints() {
    return this.json.summaryPoints || [];
  }

  wacUrlPath(args: Omit<WacUrlPathArgs, 'productKey'>): string | undefined {
    const offering = this.getOffering();
    if (!this.json.wacUrlPath) {
      return undefined;
    }
    if (!offering?.productKey) {
      throw new Error(`No offering productKey found for product ${this.productName}`);
    }

    return this.json.wacUrlPath({
      ...args,
      productKey: offering.productKey,
    });
  }

  ccpWacUrlPath(args: CcpWacUrlPathArgs): string | undefined {
    if (!this.json.ccpWacUrlPath) {
      return undefined;
    }

    return this.json.ccpWacUrlPath(args);
  }

  /**
   * Only used internally to build reverse mappings.
   *
   * @returns map of values that can be used to lookup the product config.
   */
  generateReverseMapEntries(): { [K in keyof ProductConfigInputOptions]: ProductConfigInputOptions[K][] } {
    return {
      ariResourceOwner: flatUnique(this.json.ariResourceOwner),
      type: flatUnique(
        this.type,
        this.baseJson.alternatives?.type,
        Object.values(this.featureGateOverridesPerProperty.type || {}),
        Object.values(this.featureGateOverridesPerProperty.alternatives || {}).flatMap((v) => v?.type),
      ),
      name: flatUnique(
        this.productName,
        this.baseJson.alternatives?.productName,
        Object.values(this.featureGateOverridesPerProperty.productName || {}),
        Object.values(this.featureGateOverridesPerProperty.alternatives || {}).flatMap((v) => v?.productName),
      ),
      canonicalProductKey: flatUnique(
        isExplicitlyUndefined(this.json, 'userManagementCanonicalProductKey') ? undefined : this.canonicalProductKey,
        this.baseJson.alternatives?.userManagementCanonicalProductKey,
        Object.values(this.featureGateOverridesPerProperty.userManagementCanonicalProductKey || {}),
        Object.values(this.featureGateOverridesPerProperty.alternatives || {}).flatMap((v) => v?.userManagementCanonicalProductKey),
      ),
      productId: flatUnique(
        this.json.productId ? this.json.productId : undefined,
        this.baseJson.alternatives?.productId,
        Object.values(this.featureGateOverridesPerProperty.productId || {}),
        Object.values(this.featureGateOverridesPerProperty.alternatives || {}).flatMap((v) => v?.productId),
        // TODO: Migrate UM productId off the being resolved from productId
        isExplicitlyUndefined(this.json, 'userManagementProductId') ? undefined : this.productId,
        this.baseJson.alternatives?.userManagementProductId,
        Object.values(this.featureGateOverridesPerProperty.userManagementProductId || {}),
        Object.values(this.featureGateOverridesPerProperty.alternatives || {}).flatMap((v) => v?.userManagementProductId),
      ),
      key: flatUnique(
        isExplicitlyUndefined(this.json, 'idOrgManagerProductKey') ? undefined : this.key,
        this.baseJson.alternatives?.idOrgManagerProductKey,
        Object.values(this.featureGateOverridesPerProperty.idOrgManagerProductKey || {}),
        Object.values(this.featureGateOverridesPerProperty.alternatives || {}).flatMap((v) => v?.idOrgManagerProductKey),
        this.json.enterpriseProductKey,
        Object.values(this.featureGateOverridesPerProperty.enterpriseProductKey || {}),
      ),
      billingProductKey: flatUnique(
        this.json.billingProductKey,
        this.baseJson.alternatives?.billingProductKey,
        Object.values(this.offerings).map((offering: OfferingsSchemaWithIds[string]) => offering.productKey),
        Object.values(this.featureGateOverridesPerProperty.billingProductKey || {}),
        Object.values(this.featureGateOverridesPerProperty.alternatives || {}).flatMap((v) => v?.billingProductKey),
      ),
      connectorType: flatUnique(
        this.baseJson.connectorType,
        this.baseJson.alternatives?.connectorType,
        Object.values(this.featureGateOverridesPerProperty.connectorType || {}),
        Object.values(this.featureGateOverridesPerProperty.alternatives || {}).flatMap((v) => v?.connectorType),
      ),
    };
  }

  /**
   * Helper method to get the environment for an offering.
   * Returns 'commercial' if no environment is specified.
   */
  private getOfferingEnvironment(offering: OfferingsSchemaWithIds[string]): OfferingEnvironment {
    return offering.environment || 'commercial';
  }
}

type ProductConfigReverseMap = ConfigReverseMap<ProductConfigInputOptions, ProductConfig>;

/**
 * Builds a reverse mapping of potential inputs to it's corresponding product config. The reverse mapping is used to quickly lookup product config via a particular input type (i.e. type, productId, key, etc.).
 *
 * @param config the product config map json
 * @returns reverse mapping of inputs to product config
 */
export function buildReverseMappings(config: ProductConfigMapJson): ProductConfigReverseMap {
  const mappings = {} as ProductConfigReverseMap;
  const updateReverseMap = (productConfig: ProductConfigImpl) => {
    const reverseMapEntries = productConfig.generateReverseMapEntries();
    for (const [inputType, possibleInputs] of Object.entries(reverseMapEntries)) {
      if (!mappings[inputType]) {
        mappings[inputType] = {};
      }

      for (const inputKey of possibleInputs) {
        if (inputKey in mappings[inputType]) {
          throw new Error(
            `Duplicate mapping for ${inputType} ${inputKey} in product config ${mappings[inputType][inputKey].productName} ${productConfig.productName}`,
          );
        }

        mappings[inputType][inputKey] = productConfig;
      }
    }
  };

  // Convert { featureGate: { key: ProductConfigs } } to { key: { featureGate: ProductConfigs } }
  const productOverridesByKey = Object.entries(config.productOverrides || {}).reduce<Record<string, FeatureGateOverrides>>(
    (acc, [featureGate, productOverridesMap]) => {
      for (const [key, productConfigJson] of Object.entries(productOverridesMap)) {
        if (!acc[key]) {
          acc[key] = {};
        }

        acc[key][featureGate] = productConfigJson;
      }

      return acc;
    },
    {},
  );

  // Iterate through the products and add them to the reverse map
  Object.entries(config.products)
    .map(
      ([key, productConfigJson]) =>
        new ProductConfigImpl(productConfigJson, key in productOverridesByKey ? productOverridesByKey[key] : undefined),
    )
    .forEach(updateReverseMap);

  return mappings;
}

const ReverseMappings = buildReverseMappings(loadProductConfiguration());

export function getAllProductConfigs(): ProductConfig[] {
  return [...new Set(Object.values(ReverseMappings.key))];
}

export function fromProductConfig(options: Partial<ProductConfigInputOptions>): ProductConfig {
  for (const key of ProductConfigInputOptionPriority) {
    if (options[key] && options[key] in ReverseMappings[key]) {
      return ReverseMappings[key][options[key]];
    }
  }

  if (AkFeatureGates.checkGate('aims_remove_old_product_key_convertor')) {
    if (process.env.NODE_ENV === 'development') {
      console.error('%cStop for a second!', 'color: red; font-size: 30px; font-weight: bold;'); // eslint-disable-line no-console
      // eslint-disable-next-line no-console
      console.error(
        `%c "ProductConfig" could not be determined for ${JSON.stringify(options)}! Please add a configuration entry for that in product-config-json.ts`,
        'color: red; font-size: 24px;',
      );
    }

    throw new Error(`Cannot determine product configuration of ${JSON.stringify(options)}`);
  }

  // CLEANUP TODO: For now fallback to the original ProductKeyConverter. Throw error if no product config is found
  return new ProductKeyConverter(options as ProductKeyConverterValues) as unknown as ProductConfig;
}
